import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useUserCounts } from '../contexts/UserCountsContext';
import { hasPagePermission } from '../utils/permissions';
import Subscribers from './Subscribers';
import Personnel from './role-pages/Personnel';
import Coaches from './role-pages/Coaches';
import Riders from './role-pages/Riders';
import Owners from './role-pages/Owners';
import Temporary from './role-pages/Temporary';

const TAB_ICONS = { subscribers: "🧑‍🎓", personnel: "👥", coaches: "🏇", riders: "🐎", owners: "🏠", temporary: "⏰" };

const ALL_TABS = [
  { id: "subscribers", labelKey: "navigation.subscribers", permission: "/subscribers", Component: Subscribers, countKey: "subscribers" },
  { id: "personnel",   labelKey: "navigation.personnel",   permission: "/personnel",   Component: Personnel,   countKey: "personnel"   },
  { id: "coaches",     labelKey: "navigation.coaches",     permission: "/coaches",     Component: Coaches,     countKey: "coaches"     },
  { id: "riders",      labelKey: "navigation.riders",      permission: "/riders",      Component: Riders,      countKey: "riders"      },
  { id: "owners",      labelKey: "navigation.owners",      permission: "/owners",      Component: Owners,      countKey: "owners"      },
  { id: "temporary",   labelKey: "navigation.temporaryUsers", permission: "/temporary", Component: Temporary, countKey: "temporary"  },
];

export default function UsersHub() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { userCounts } = useUserCounts();
  const [searchParams, setSearchParams] = useSearchParams();
  const isRTL = i18n.language === "ar";
  const tabBarRef = useRef(null);
  const animatingRef = useRef(false);

  const tabs = ALL_TABS.filter(tab => hasPagePermission(user, tab.permission));
  const tabFromUrl = searchParams.get("tab");
  const defaultTab = tabs.length > 0 ? tabs[0].id : "subscribers";
  const initialTab = tabs.find(t => t.id === tabFromUrl) ? tabFromUrl : defaultTab;

  const [activeTab, setActiveTab] = useState(initialTab);
  const [animating, setAnimating] = useState(false);
  const [slideDirection, setSlideDirection] = useState("right");
  const prevIndexRef = useRef(tabs.findIndex(t => t.id === initialTab));

  useEffect(() => {
    const urlTab = searchParams.get("tab");
    if (urlTab && urlTab !== activeTab && tabs.find(t => t.id === urlTab)) {
      const newIdx = tabs.findIndex(t => t.id === urlTab);
      setSlideDirection(newIdx > prevIndexRef.current ? "right" : "left");
      setAnimating(true); animatingRef.current = true;
      setActiveTab(urlTab);
      prevIndexRef.current = newIdx;
      setTimeout(() => { setAnimating(false); animatingRef.current = false; }, 320);
    }
  }, [searchParams]); // eslint-disable-line

  const handleTabChange = useCallback((newTabId) => {
    if (newTabId === activeTab || animatingRef.current) return;
    const newIdx = tabs.findIndex(t => t.id === newTabId);
    setSlideDirection(newIdx > prevIndexRef.current ? "right" : "left");
    setAnimating(true); animatingRef.current = true;
    setActiveTab(newTabId);
    setSearchParams({ tab: newTabId }, { replace: true });
    prevIndexRef.current = newIdx;
    if (tabBarRef.current) {
      const el = tabBarRef.current.children[newIdx];
      if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
    setTimeout(() => { setAnimating(false); animatingRef.current = false; }, 320);
  }, [activeTab, setSearchParams, tabs]); // eslint-disable-line

  if (tabs.length === 0) return (
    <div className="flex items-center justify-center h-64">
      <p className="text-neutral-500 text-sm">No access</p>
    </div>
  );

  const ActiveComponent = tabs.find(t => t.id === activeTab)?.Component;

  return (
    <div className="flex flex-col h-full min-h-0">
      <style>{`
        @keyframes hubSlideInRight{from{transform:translateX(48px);opacity:0}to{transform:translateX(0);opacity:1}}
        @keyframes hubSlideInLeft{from{transform:translateX(-48px);opacity:0}to{transform:translateX(0);opacity:1}}
        .hub-slide-right{animation:hubSlideInRight 0.28s cubic-bezier(0.22,1,0.36,1) both}
        .hub-slide-left{animation:hubSlideInLeft 0.28s cubic-bezier(0.22,1,0.36,1) both}
        .users-hub-tabbar::-webkit-scrollbar{display:none}
      `}</style>
      <div className="flex-shrink-0 bg-white border-b border-gray-100 shadow-sm">
        <div ref={tabBarRef} className="users-hub-tabbar flex overflow-x-auto px-2 sm:px-4"
          style={{ scrollbarWidth:"none", msOverflowStyle:"none", direction: isRTL?"rtl":"ltr" }}>
          {tabs.map(tab => {
            const isActive = tab.id === activeTab;
            const count = userCounts?.[tab.countKey];
            return (
              <button key={tab.id} onClick={() => handleTabChange(tab.id)}
                className={[
                  "relative flex items-center gap-2 px-4 py-3.5 text-sm font-medium whitespace-nowrap",
                  "transition-all duration-200 border-b-2 flex-shrink-0 select-none",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400",
                  isActive
                    ? "border-primary-500 text-primary-700 bg-primary-50/30"
                    : "border-transparent text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50 hover:border-neutral-200",
                ].join(" ")}>
                <span className="text-base leading-none">{TAB_ICONS[tab.id]}</span>
                <span>{t(tab.labelKey)}</span>
                {count !== undefined && count > 0 && (
                  <span className={[
                    "inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold leading-none",
                    tab.id === "subscribers" && count > 0
                      ? "bg-red-500 text-white animate-pulse"
                      : isActive ? "bg-primary-200 text-primary-800" : "bg-neutral-100 text-neutral-600",
                  ].join(" ")}>{count}</span>
                )}
                {isActive && (
                  <span className="absolute bottom-0 left-1 right-1 h-[2px] rounded-full"
                    style={{ background:"linear-gradient(90deg,var(--color-primary-400,#6366f1),var(--color-primary-600,#4f46e5),var(--color-primary-400,#6366f1))" }} />
                )}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex-1 overflow-hidden relative min-h-0">
        <div key={activeTab}
          className={["h-full overflow-y-auto", animating?(slideDirection==="right"?"hub-slide-right":"hub-slide-left"):""].join(" ")}>
          {ActiveComponent && <ActiveComponent />}
        </div>
      </div>
    </div>
  );
}
